import { useContext } from "react"
import styled from "styled-components"
import { useLocale, useT } from "react-polyglot-hooks"
import { PrimaryButton } from "components/styled/Buttons/PrimaryButton"
import Typography from "components/styled/Typography"
import { scrollToRef } from "helpers/scroll"
import { color, hideAt, mq } from "helpers/styled"
import { CarContext } from "scenes/CarScene/CarContext"
import Link from "components/Link"
import { sendEvent } from "scripts/GoogleAnalytics"
import kebabCase from "lodash/kebabCase"

const Inner = styled.div`
  ${hideAt("lg")};
  position: relative;
  margin: 0 -15px 25px;
  padding: 15px;
  background-color: ${color("paleGray")};
  border: 1px solid ${color("lightGray")};
  border-radius: 12px;

  @media ${mq("md")} {
    margin: 0 0 25px;
  }
`

const CtaInfo = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  > :first-child {
    margin-right: auto;
    padding-right: 10px;
  }

  > :last-child {
    margin-left: auto;
  }
`

const Disclaimer = styled.div`
  margin: 0 0 20px auto;
  text-align: center;
  flex: 1 0 300px;

  > * {
    margin: auto;
    max-width: 300px;

    @media ${mq("sm")} {
      margin-right: 0;
    }
  }
`

const From = styled(Typography)`
  display: flex;
  margin-bottom: 10px;
  justify-content: flex-end;

  > span {
    display: inline-block;
    margin-left: 5px;
  }
`

const OldPrice = styled.span`
  color: ${color("mediumVioletRed")};
  text-decoration: line-through;
`

const Title = styled(Typography)`
  font-size: 21px;
  line-height: 24px;
  margin-bottom: 5px;

  @media ${mq("md")} {
    font-size: 30px;
    line-height: 30px;
  }
`

const FlexibleTitle = styled(Typography).attrs({
  el: "span"
})`
  font-size: 14px;
  line-height: 24px;
  color: grey;

  @media ${mq("md")} {
    margin: 0;
    font-size: 21px;
    line-height: 30px;
  }
`

const Subtitle = styled(Typography).attrs({
  el: "span"
})`
  font-size: 14px;
  line-height: 24px;

  @media ${mq("md")} {
    margin: 0;
    font-size: 21px;
    line-height: 30px;
  }
`

const PriceWrap = styled.div`
  text-align: right;
`

const Price = styled(Typography)`
  display: flex;
  align-items: baseline;
  font-size: 30px;
  line-height: 24px;

  @media ${mq("md")} {
    font-size: 36px;
    line-height: 30px;
  }

  span {
    margin-left: 5px;
    font-size: 21px;
    line-height: inherit;
  }
`

const Italic = styled(Typography)`
  font-style: italic;
`

const Button = styled(PrimaryButton)`
  width: 100%;
  margin-top: 15px;
  margin-right: 0;

  @media ${mq("sm")} {
    width: 300px;
  }
`

function MobileCTA() {
  const {
    page,
    plans,
    plansRef,
    selectedPlan,
    mobileCtaRef,
    withoutTaxes,
    car: { brand, model, version, promotionOldPrice, sold, downPayment }
  } = useContext(CarContext)
  const t = useT()
  const locale = useLocale()

  const handleClick = () => {
    sendEvent("Click CTA prices", { event_category: page, event_label: "prices" })
    scrollToRef(plansRef)
  }

  return (
    <Inner ref={mobileCtaRef}>
      <CtaInfo>
        <div>
          <Title variant="title2" el="h2" weight="bold">
            <FlexibleTitle>Renting</FlexibleTitle>{" "}
            <span>{`${brand} ${model} `}</span>
            <Subtitle>{version}</Subtitle>
          </Title>
        </div>

        {sold ? (
          <Disclaimer>
            <Typography color="darkBlue" variant="title4" weight="bold">
              {t("car_data.sold")}
            </Typography>
          </Disclaimer>
        ) : (
          <PriceWrap>
            <From weight="medium">
              {t("common.from")}
              {promotionOldPrice ? (
                <OldPrice>{`${promotionOldPrice} €`}</OldPrice>
              ) : (
                <Italic variant="smallPrice" el="span">
                  {`(${t(withoutTaxes ? "common.without_tax" : "common.tax_included")})`}
                </Italic>
              )}
            </From>
            <Price variant="title1" el="span" weight="bold" color="skyBlue">
              {withoutTaxes ? selectedPlan.priceWithoutTaxes : selectedPlan.price}
              <Typography variant="smallPrice" weight="bold" el="span" color="skyBlue">
                {t("common.currency")}
              </Typography>
            </Price>
            {locale === "it" && (
              <Italic color="skyBlue" variant="small" weight="bold">
                {downPayment ? t("common.down_payment", { downPayment }) : t("common.without_down_payment")}
              </Italic>
            )}
          </PriceWrap>
        )}
      </CtaInfo>

      {sold ? (
        <Link page="brand" params={{ brand: kebabCase(brand) }}>
          <Button as="a" size="lg" blue shadow>
            {t("common.similar-offers")}
          </Button>
        </Link>
      ) : (
        <Button size="lg" onClick={handleClick}>
          {t(plans.length > 1 ? "common.view_more_prices" : "car_data.plans.cta")}
        </Button>
      )}
    </Inner>
  )
}

export default MobileCTA
